"use client";

import { SignInPage } from "@/components/ui/sign-in";
import { createClient } from "@/lib/supabase/client";
import {
  isPortalAccessAllowed,
  type PortalBranding,
} from "@/lib/portal-branding";
import { track } from "@/lib/analytics";
import { Loader2 } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

interface LoginFormProps {
  branding: PortalBranding;
}

export function LoginForm({ branding }: LoginFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const shouldLogout = searchParams.get("logout") === "true";
    const errorParam = searchParams.get("error");
    const isUnauthorized = errorParam === "unauthorized";
    const isPortalMismatch = errorParam === "portal";

    if (shouldLogout || isUnauthorized || isPortalMismatch) {
      const supabase = createClient();
      supabase.auth.signOut().then(() => {
        if (isPortalMismatch) {
          setError(branding.accessDeniedMessage);
        } else if (isUnauthorized) {
          setError(branding.unauthorizedMessage);
        }
        router.replace("/auth/login");
      });
    }
  }, [
    searchParams,
    router,
    branding.unauthorizedMessage,
    branding.accessDeniedMessage,
  ]);

  useEffect(() => {
    if (searchParams.get("logout") === "true" || searchParams.get("error")) {
      return;
    }

    let active = true;

    (async () => {
      const supabase = createClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session || !active) return;

      const meRes = await fetch("/api/auth/me", { credentials: "include" });
      const me = await meRes.json().catch(() => null);
      const role: string | undefined = me?.data?.role ?? me?.role;
      if (!active) return;

      if (isPortalAccessAllowed(window.location.host, role)) {
        setIsRedirecting(true);
        router.replace("/overview");
      } else {
        await supabase.auth.signOut();
        if (active) setError(branding.accessDeniedMessage);
      }
    })();

    return () => {
      active = false;
    };
  }, [searchParams, router, branding.accessDeniedMessage]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    const supabase = createClient();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      const meRes = await fetch("/api/auth/me", {
        credentials: "include",
      });
      const me = await meRes.json().catch(() => null);
      const role: string | undefined = me?.data?.role ?? me?.role;

      if (!isPortalAccessAllowed(window.location.host, role)) {
        await supabase.auth.signOut();
        setError(branding.accessDeniedMessage);
        setIsLoading(false);
        return;
      }

      track("admin_login", { role: role ?? "unknown" });
      setIsRedirecting(true);
      let target = "/";
      if (
        role === "INTERSTUD_ADMIN" ||
        role === "SCHOOL_ADMIN" ||
        role === "ADVISOR"
      ) {
        target = "/overview";
      } else if (role === "USER") {
        target = "/unauthorized";
      }
      router.replace(target);
    } catch (error: unknown) {
      setError(
        error instanceof Error ? error.message : "Une erreur est survenue",
      );
      setIsLoading(false);
    }
  };

  if (isRedirecting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Connexion réussie...</p>
        </div>
      </div>
    );
  }

  return (
    <SignInPage
      title={
        <span className="font-light text-foreground tracking-tighter">
          {branding.title}
        </span>
      }
      description={branding.description}
      errorMessage={error}
      isLoading={isLoading}
      onSignIn={handleLogin}
      onResetPassword={() => router.push("/auth/forgot-password")}
    />
  );
}
